// Canonicalise a capture_items quantity to the backend-accepted form — the fix
// for the production offline-upload JAM (powersync_upload_jam, Sentry
// CITYCARE-ASSET-CAPTURE-1G).
//
// THE BUG. The measurement NumericKeypadSheet (ui/numeric-keypad-sheet.tsx)
// fires `onChange` on EVERY keystroke and emits non-canonical intermediates the
// surveyor never "finishes":
//   - the decimal tap emits a TRAILING-DOT draft — "5." / "0." (appendDecimal)
//   - Clear emits the EMPTY string "" (an intentional, TESTED local-display
//     state — asset-row.test.tsx "clearing back to empty")
// asset-row passes these straight through and the 500 ms capture-client debounce
// COMMITS whatever is on screen, so a >500 ms pause after the dot (a surveyor
// glancing at a tape measure) persists "5." and queues it as a capture_items
// PATCH. The backend quantity regex /^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/
// (apps/backend/src/capture/admin-schemas.ts) rejects "5." / "" with
// invalid_argument(400); that code is NOT permanent for capture_items in the
// central classifier (connector-upload-isolation.ts:
// PERMANENT_UPLOAD_CODES = {'not_found'}), so dispatchCaptureItemPatch rethrows
// → uploadData never reaches batch.complete() → the SDK retries the WHOLE batch
// every ~5 s forever and every later capture on the device is stranded while the
// UI shows "✓ Saved".
//
// THE FIX, AT TWO LAYERS.
//   1. COMMIT BOUNDARY (the root): the capture client canonicalises before the
//      optimistic SQLite write, so a half-typed draft NEVER becomes a persisted
//      or synced row. "5." commits as "5"; the surveyor's count is correct
//      locally and on the server.
//   2. CONNECTOR SEND (recovery + defence): the connector canonicalises the
//      outgoing PATCH body, so a device ALREADY wedged with a queued "5."
//      (created before this fix shipped) recovers on the next upload cycle and
//      LANDS the count — it is not dropped.
//
// WHY CANONICALISE rather than drain/quarantine the PATCH. The row a PATCH
// targets EXISTS server-side, so quarantineUnsyncableCount would DELETE a row
// the server still holds (the Codex PR 656 trap that deliberately keeps
// capture_items invalid_argument out of the central drain). Repairing "5." → "5"
// makes the PATCH LAND at the intended count — no delete, no quarantine, no
// recovery step.
//
// CONSERVATIVE BY CONSTRUCTION. We never ROUND (that would change the
// contractual count — AGENTS.md "Business criticality"): a value that is not a
// keypad-shaped repairable (e.g. >2 fraction digits, negative, non-numeric)
// returns null, and callers forward the original so the backend regex — not this
// layer — rejects it. The repairs are exactly the reversible normalisations:
// strip ONE trailing '.', prefix a leading '.', and fold ""/"." to "0".

/** Canonical capture_items quantity: a non-negative decimal, ≤2 fraction digits
 * (mirrors numeric(10,2) and the backend NUMERIC_QUANTITY_2DP). Byte-identical to
 * the backend validators (apps/backend/src/capture/admin-schemas.ts +
 * apps/backend/src/admin/capture-item-schemas.ts) — the literal equality is
 * ENFORCED by lint:powersync-quantity-canonical's checkRegexParity (audit-1 #3);
 * a backend regex change that desyncs this reddens that gate. */
export const QUANTITY_CANONICAL_RE = /^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/

/**
 * Repair a keypad-shaped quantity draft into the backend-canonical form, or
 * return null when it cannot be repaired WITHOUT changing the count.
 *
 *   "5." / "0."  → "5" / "0"   (trailing-dot draft — the live wedge)
 *   ""  / "."    → "0"         (Clear / lone dot — empty measurement = 0)
 *   ".5"         → "0.5"       (leading dot — defensive; the keypad pre-prefixes)
 *   "5" / "5.50" → unchanged   (already canonical)
 *   "5.555" / "-1" / "x" → null (NOT rounded/clamped — caller forwards as-is)
 */
export function canonicalizeQuantity(raw: string): string | null {
  let s = raw.trim()
  if (s === '' || s === '.') return '0'
  if (s.startsWith('.')) s = `0${s}` // ".5" → "0.5"
  if (s.endsWith('.')) s = s.slice(0, -1) // "5." → "5"
  return QUANTITY_CANONICAL_RE.test(s) ? s : null
}

/**
 * Apply `canonicalizeQuantity` to a CRUD opData quantity at a connector SEND
 * site. Passes a non-string (undefined — a thin PATCH that omits an unchanged
 * quantity, or a number) through untouched, and forwards an UN-repairable string
 * unchanged so the backend regex — not this layer — is the one that rejects it
 * (never silently alter a count). Every capture_items quantity the connector
 * PUTs MUST go through this (enforced by lint:powersync-quantity-canonical).
 */
export function canonicalizeForUpload(raw: unknown): unknown {
  if (typeof raw !== 'string') return raw
  return canonicalizeQuantity(raw) ?? raw
}
